<template>
  <div class="message-conversion-report">
    <a-tabs v-model:activeKey="activeMessageType" class="message-conversion-sub-tabs">
      <a-tab-pane key="certificate" tab="📬 인증서 알림톡">
        <a-table
          :columns="certificateColumns"
          :data-source="filterByMessageType('certificate')"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1800 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'date'">
              <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
            </template>
            <template v-else-if="column.key === 'messageType'">
              <span>{{ messageTypeLabels[record.messageType] || record.messageType }}</span>
            </template>
            <template v-else-if="column.key === 'sent'">
              <span
                v-if="record.messageType === 'certificate'"
                class="link-text"
                @click="navigateToCertificateList(record.date)"
              >
                <AdjustmentInput
                  :base-value="record.sent || 0"
                  :adjustment-value="getAdjustment('certificate', record.date, 'sent')"
                  :is-editing="isEditing('certificate', record.date, 'sent')"
                  :formatter="formatNumber"
                  @start-edit="startEditing('certificate', record.date, 'sent')"
                  @confirm="(val) => confirmEditWithValue('certificate', record.date, 'sent', val)"
                  @cancel="cancelEdit"
                />
              </span>
              <span v-else>
                <AdjustmentInput
                  :base-value="record.sent || 0"
                  :adjustment-value="getAdjustment('certificate', record.date, 'sent')"
                  :is-editing="isEditing('certificate', record.date, 'sent')"
                  :formatter="formatNumber"
                  @start-edit="startEditing('certificate', record.date, 'sent')"
                  @confirm="(val) => confirmEditWithValue('certificate', record.date, 'sent', val)"
                  @cancel="cancelEdit"
                />
              </span>
            </template>
            <template v-else-if="column.key === 'clicks'">
              <span>{{ formatClicks(record.uniqueClicks, record.clicks) }}</span>
            </template>
            <template v-else-if="column.key === 'clickRate'">
              <span>{{ formatPercentage(record.clickRate) }}</span>
            </template>
            <template v-else-if="column.key === 'eventPageVisits'">
              <AdjustmentInput
                :base-value="record.eventPageVisits || 0"
                :adjustment-value="getAdjustment('certificate', record.date, 'eventPageVisits')"
                :is-editing="isEditing('certificate', record.date, 'eventPageVisits')"
                :formatter="formatNumber"
                @start-edit="startEditing('certificate', record.date, 'eventPageVisits')"
                @confirm="(val) => confirmEditWithValue('certificate', record.date, 'eventPageVisits', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'shares'">
              <AdjustmentInput
                :base-value="record.shares || 0"
                :adjustment-value="getAdjustment('certificate', record.date, 'shares')"
                :is-editing="isEditing('certificate', record.date, 'shares')"
                :formatter="formatNumber"
                @start-edit="startEditing('certificate', record.date, 'shares')"
                @confirm="(val) => confirmEditWithValue('certificate', record.date, 'shares', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'referredQuizParticipants'">
              <span>{{ formatNumber(record.referredQuizParticipants || 0) }}</span>
            </template>
            <template v-else-if="column.key === 'couponIssuances'">
              <span>{{ formatNumber(record.couponIssuances || 0) }}</span>
            </template>
          </template>
          <template #summary>
            <a-table-summary>
              <a-table-summary-row class="summary-row">
                <a-table-summary-cell index="0" :col-span="2">
                  <strong>합계/평균</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="1" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('certificate'), 'certificate', 'sent')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ calculateSumClicks(filterByMessageType('certificate')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ calculateClickRateFromSum(filterByMessageType('certificate')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="4" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('certificate'), 'certificate', 'eventPageVisits')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="5" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('certificate'), 'certificate', 'shares')) }}</strong>
                </a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-tab-pane>

      <a-tab-pane key="review" tab="✍️ 후기 알림톡">
        <a-table
          :columns="reviewColumns"
          :data-source="filterByMessageType('review')"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1800 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'date'">
              <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
            </template>
            <template v-else-if="column.key === 'messageType'">
              <span>{{ messageTypeLabels[record.messageType] || record.messageType }}</span>
            </template>
            <template v-else-if="column.key === 'sent'">
              <AdjustmentInput
                :base-value="record.sent || 0"
                :adjustment-value="getAdjustment('review', record.date, 'sent')"
                :is-editing="isEditing('review', record.date, 'sent')"
                :formatter="formatNumber"
                @start-edit="startEditing('review', record.date, 'sent')"
                @confirm="(val) => confirmEditWithValue('review', record.date, 'sent', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'clicks'">
              <span>{{ formatClicks(record.uniqueClicks, record.clicks) }}</span>
            </template>
            <template v-else-if="column.key === 'clickRate'">
              <span>{{ formatPercentage(record.clickRate) }}</span>
            </template>
            <template v-else-if="column.key === 'reviewSubmissions'">
              <AdjustmentInput
                :base-value="record.reviewSubmissions || 0"
                :adjustment-value="getAdjustment('review', record.date, 'reviewSubmissions')"
                :is-editing="isEditing('review', record.date, 'reviewSubmissions')"
                :formatter="formatNumber"
                @start-edit="startEditing('review', record.date, 'reviewSubmissions')"
                @confirm="(val) => confirmEditWithValue('review', record.date, 'reviewSubmissions', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'shares'">
              <AdjustmentInput
                :base-value="record.shares || 0"
                :adjustment-value="getAdjustment('review', record.date, 'shares')"
                :is-editing="isEditing('review', record.date, 'shares')"
                :formatter="formatNumber"
                @start-edit="startEditing('review', record.date, 'shares')"
                @confirm="(val) => confirmEditWithValue('review', record.date, 'shares', val)"
                @cancel="cancelEdit"
              />
            </template>
          </template>
          <template #summary>
            <a-table-summary>
              <a-table-summary-row class="summary-row">
                <a-table-summary-cell index="0" :col-span="2">
                  <strong>합계/평균</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="1" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('review'), 'review', 'sent')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ calculateSumClicks(filterByMessageType('review')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ calculateClickRateFromSum(filterByMessageType('review')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="4" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('review'), 'review', 'reviewSubmissions')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="5" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('review'), 'review', 'shares')) }}</strong>
                </a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-tab-pane>

      <a-tab-pane key="friend" tab="🏥 병원예약 유도 친구톡">
        <a-table
          :columns="friendColumns"
          :data-source="filterByMessageType('friend')"
          :loading="loading"
          :pagination="false"
          :scroll="{ x: 1800 }"
        >
          <template #bodyCell="{ column, record }">
            <template v-if="column.key === 'date'">
              <span :class="getDateClass(record.date)">{{ formatDate(record.date) }}</span>
            </template>
            <template v-else-if="column.key === 'messageType'">
              <span>{{ messageTypeLabels[record.messageType] || record.messageType }}</span>
            </template>
            <template v-else-if="column.key === 'sent'">
              <AdjustmentInput
                :base-value="record.sent || 0"
                :adjustment-value="getAdjustment('friend', record.date, 'sent')"
                :is-editing="isEditing('friend', record.date, 'sent')"
                :formatter="formatNumber"
                @start-edit="startEditing('friend', record.date, 'sent')"
                @confirm="(val) => confirmEditWithValue('friend', record.date, 'sent', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'quizParticipants'">
              <AdjustmentInput
                :base-value="record.quizParticipants || 0"
                :adjustment-value="getAdjustment('friend', record.date, 'quizParticipants')"
                :is-editing="isEditing('friend', record.date, 'quizParticipants')"
                :formatter="formatNumber"
                @start-edit="startEditing('friend', record.date, 'quizParticipants')"
                @confirm="(val) => confirmEditWithValue('friend', record.date, 'quizParticipants', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'hospitalSelections'">
              <AdjustmentInput
                :base-value="record.hospitalSelections || 0"
                :adjustment-value="getAdjustment('friend', record.date, 'hospitalSelections')"
                :is-editing="isEditing('friend', record.date, 'hospitalSelections')"
                :formatter="formatNumber"
                @start-edit="startEditing('friend', record.date, 'hospitalSelections')"
                @confirm="(val) => confirmEditWithValue('friend', record.date, 'hospitalSelections', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'customerInfoInputs'">
              <AdjustmentInput
                :base-value="record.customerInfoInputs || 0"
                :adjustment-value="getAdjustment('friend', record.date, 'customerInfoInputs')"
                :is-editing="isEditing('friend', record.date, 'customerInfoInputs')"
                :formatter="formatNumber"
                @start-edit="startEditing('friend', record.date, 'customerInfoInputs')"
                @confirm="(val) => confirmEditWithValue('friend', record.date, 'customerInfoInputs', val)"
                @cancel="cancelEdit"
              />
            </template>
            <template v-else-if="column.key === 'reservations'">
              <AdjustmentInput
                :base-value="record.reservations || 0"
                :adjustment-value="getAdjustment('friend', record.date, 'reservations')"
                :is-editing="isEditing('friend', record.date, 'reservations')"
                :formatter="formatNumber"
                @start-edit="startEditing('friend', record.date, 'reservations')"
                @confirm="(val) => confirmEditWithValue('friend', record.date, 'reservations', val)"
                @cancel="cancelEdit"
              />
            </template>
          </template>
          <template #summary>
            <a-table-summary>
              <a-table-summary-row class="summary-row">
                <a-table-summary-cell index="0" :col-span="2">
                  <strong>합계/평균</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="1" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('friend'), 'friend', 'sent')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="2" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('friend'), 'friend', 'quizParticipants')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="3" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('friend'), 'friend', 'hospitalSelections')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="4" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('friend'), 'friend', 'customerInfoInputs')) }}</strong>
                </a-table-summary-cell>
                <a-table-summary-cell index="5" align="right">
                  <strong>{{ formatNumber(calculateAdjustedSum(filterByMessageType('friend'), 'friend', 'reservations')) }}</strong>
                </a-table-summary-cell>
              </a-table-summary-row>
            </a-table-summary>
          </template>
        </a-table>
      </a-tab-pane>
    </a-tabs>
  </div>
</template>

<script>
import { ref, computed, h, watch } from 'vue';
import { useRouter } from 'vue-router';
import { Table, Tabs, Tooltip, message } from 'ant-design-vue';
import { QuestionCircleOutlined } from '@ant-design/icons-vue';
import {
  messageTypeLabels,
  formatNumber,
  formatPercentage,
  formatDate,
  displayValue,
  sortByMessageType,
  formatClicks,
  getDayOfWeek
} from '../utils';
import AdjustmentInput from './AdjustmentInput.vue';
import { useMessageAdjustments } from '../composables/useMessageAdjustments';

export default {
  name: 'MessageConversionReport',
  components: {
    ATable: Table,
    ATabs: Tabs,
    ATabPane: Tabs.TabPane,
    ATableSummary: Table.Summary,
    ATableSummaryRow: Table.Summary.Row,
    ATableSummaryCell: Table.Summary.Cell,
    AdjustmentInput,
  },
  props: {
    reportData: {
      type: Array,
      default: () => []
    },
    reportId: {
      type: String,
      default: null
    },
    api: {
      type: Object,
      default: null
    }
  },
  setup(props) {
    const router = useRouter();
    const loading = ref(false);
    const activeMessageType = ref('certificate');

    // 보정치 컴포저블 초기화
    const adjustmentApi = computed(() => props.api || {
      updateAdjustments: () => {},
      getAdjustments: () => {}
    });

    const {
      messageAdjustments,
      editingCell,
      tempInputValue,
      loading: adjustmentLoading,
      getAdjustment,
      hasAdjustment,
      getAdjustedValue,
      startEditing,
      cancelEdit,
      confirmEdit,
      handleKeydown,
      isEditing,
      calculateAdjustedSum,
      loadMessageAdjustments,
      setReportId
    } = useMessageAdjustments(adjustmentApi.value);

    // reportId 변경 시 보정치 로드
    watch(
      () => props.reportId,
      (newReportId) => {
        if (newReportId && props.api) {
          loadMessageAdjustments(newReportId);
        }
      },
      { immediate: true }
    );

    // 보정치 저장 (컴포넌트에서 값을 받아서 처리)
    const confirmEditWithValue = (messageType, date, field, newValue) => {
      if (!editingCell.value) return;

      tempInputValue.value = newValue;
      confirmEdit();
    };

    // 공통 컬럼 정의
    const baseColumns = [
      {
        title: '날짜',
        dataIndex: 'date',
        key: 'date',
        width: 100,
        fixed: 'left',
      },
      {
        title: '메시지유형',
        dataIndex: 'messageType',
        key: 'messageType',
        width: 130,
      },
      {
        title: '발송건수',
        dataIndex: 'sent',
        key: 'sent',
        width: 100,
        align: 'right',
      },
      {
        title: '알림톡클릭수(중복건수)',
        dataIndex: 'clicks',
        key: 'clicks',
        width: 170,
        align: 'right',
      },
      {
        title: '클릭률',
        dataIndex: 'clickRate',
        key: 'clickRate',
        width: 100,
        align: 'right',
      },
      {
        title: '이벤트페이지유입수',
        dataIndex: 'eventPageVisits',
        key: 'eventPageVisits',
        width: 150,
        align: 'right',
      },
    ];

    // 인증서/후기 알림톡 컬럼 (후기 작성 퍼널)
    const certificateColumns = [
      ...baseColumns,
      {
        title: '공유수',
        dataIndex: 'shares',
        key: 'shares',
        width: 120,
        align: 'right',
      },
    ];

    // 후기 알림톡 컬럼 (후기 작성 퍼널)
    // eventPageVisits 제외한 baseColumns 사용
    const reviewBaseColumns = baseColumns.filter(col => col.key !== 'eventPageVisits');
    const reviewColumns = [
      ...reviewBaseColumns,
      {
        title: '후기작성수',
        dataIndex: 'reviewSubmissions',
        key: 'reviewSubmissions',
        width: 110,
        align: 'right',
      },
      {
        title: '공유수',
        dataIndex: 'shares',
        key: 'shares',
        width: 120,
        align: 'right',
      },
    ];

    // 친구톡 컬럼 (병원 예약 퍼널)
    // baseColumns의 clicks, clickRate, eventPageVisits 컬럼 제외
    const friendBaseColumns = baseColumns
      .filter(col => col.key !== 'eventPageVisits' && col.key !== 'clickRate' && col.key !== 'clicks')
      .map(col => {
        if (col.key === 'sent') {
          return {
            ...col,
            title: () => h('span', [
              '발송건수 ',
              h(Tooltip, {
                title: '친구톡은 서티라이프 카카오 채널을 팔로우한 사용자에게만 발송됩니다. 따라서 실제 발송 건수와 수신 건수 간에 차이가 발생할 수 있습니다.'
              }, () => h(QuestionCircleOutlined, { style: { color: '#1890ff', cursor: 'pointer' } }))
            ])
          };
        }
        return col;
      });
    const friendColumns = [
      ...friendBaseColumns,
      {
        title: () => h('span', [
          '퀴즈참여수 ',
          h(Tooltip, {
            title: '스마일 환자의 카카오톡 공유하기 및 N차 공유를 통한 퀴즈 이벤트 참여자 수'
          }, () => h(QuestionCircleOutlined, { style: { color: '#1890ff', cursor: 'pointer' } }))
        ]),
        dataIndex: 'quizParticipants',
        key: 'quizParticipants',
        width: 110,
        align: 'right',
      },
      {
        title: '병원선택수',
        dataIndex: 'hospitalSelections',
        key: 'hospitalSelections',
        width: 130,
        align: 'right',
      },
      {
        title: '고객정보입력수',
        dataIndex: 'customerInfoInputs',
        key: 'customerInfoInputs',
        width: 150,
        align: 'right',
      },
      {
        title: '병원예약수',
        dataIndex: 'reservations',
        key: 'reservations',
        width: 110,
        align: 'right',
      },
    ];

    // 전체 데이터 정렬: 날짜 오름차순 -> 메시지 타입 순서
    const processedData = computed(() => {
      if (!props.reportData || props.reportData.length === 0) return [];

      return [...props.reportData].sort((a, b) => {
        const dateCompare = new Date(a.date) - new Date(b.date);
        if (dateCompare !== 0) return dateCompare;
        return sortByMessageType(a, b);
      });
    });

    // 세그먼트별 필터링
    const filterByMessageType = (messageType) => {
      if (!props.reportData || props.reportData.length === 0) return [];

      // messageType 기반 필터링
      if (messageType === 'certificate') {
        // 신규 인증서 발급자
        return processedData.value.filter(item =>
          item.messageType === 'certificate'
        );
      } else if (messageType === 'review') {
        // 기존 인증서 보유자
        return processedData.value.filter(item =>
          item.messageType === 'review'
        );
      } else if (messageType === 'friend') {
        // 인증서 미보유자
        return processedData.value.filter(item =>
          item.messageType === 'friend'
        );
      }

      return [];
    };

    // 인증서 리스트로 이동
    const navigateToCertificateList = (date) => {
      // date가 이미 YYYY-MM-DD 형식이므로 그대로 사용
      router.push({
        path: '/certificate/list',
        query: {
          startDate: date,
          endDate: date,
          prevPage: '/events/participation'
        }
      });
    };

    // 날짜 컬럼 색상 클래스
    const getDateClass = (dateStr) => {
      const dayOfWeek = getDayOfWeek(dateStr);
      if (dayOfWeek === 0) return 'date-sunday'; // 일요일
      if (dayOfWeek === 6) return 'date-saturday'; // 토요일
      return '';
    };

    // 클릭률 포맷팅 (퍼센트 단위로 변환)
    const formatClickRatePercent = (clickRate) => {
      if (clickRate === null || clickRate === undefined) return '-';
      if (typeof clickRate !== 'number') return clickRate;
      // 소수점 값(0.5 등)을 퍼센트로 변환 (50% 등)
      return `${(clickRate * 100).toFixed(2)}%`;
    };

    // 합계 계산 함수
    const calculateSum = (data, field) => {
      if (!data || data.length === 0) return 0;
      return data.reduce((sum, item) => sum + (item[field] || 0), 0);
    };

    // 평균 계산 함수 (비율 필드용 - 소수점 유지)
    const calculateAverage = (data, field) => {
      if (!data || data.length === 0) return 0;
      const sum = calculateSum(data, field);
      return sum / data.length;
    };

    // 클릭수 합계 계산 (uniqueClicks(clicks) 형식)
    const calculateSumClicks = (data) => {
      if (!data || data.length === 0) return '-';
      const totalUnique = calculateSum(data, 'uniqueClicks');
      const totalClicks = calculateSum(data, 'clicks');
      return formatClicks(totalUnique, totalClicks);
    };

    // 클릭수 평균 계산 (uniqueClicks(clicks) 형식) - 건수는 반올림
    const calculateAverageClicks = (data) => {
      if (!data || data.length === 0) return '-';
      const avgUnique = Math.round(calculateAverage(data, 'uniqueClicks'));
      const avgClicks = Math.round(calculateAverage(data, 'clicks'));
      return formatClicks(avgUnique, avgClicks);
    };

    // 합계 기반 클릭률 계산 (합계 클릭수 / 합계 발송건수)
    const calculateClickRateFromSum = (data) => {
      if (!data || data.length === 0) return formatClickRatePercent(0);
      const totalClicks = calculateSum(data, 'uniqueClicks');
      const totalSent = calculateSum(data, 'sent');
      if (totalSent === 0) return formatClickRatePercent(0);
      return formatClickRatePercent(totalClicks / totalSent);
    };

    // 합계 기반 전환율 계산 (합계 예약수 / 합계 발송건수)
    const calculateConversionRateFromSum = (data) => {
      if (!data || data.length === 0) return formatClickRatePercent(0);
      const totalReservations = calculateSum(data, 'reservations');
      const totalSent = calculateSum(data, 'sent');
      if (totalSent === 0) return formatClickRatePercent(0);
      return formatClickRatePercent(totalReservations / totalSent);
    };

    return {
      loading,
      activeMessageType,
      certificateColumns,
      reviewColumns,
      friendColumns,
      processedData,
      filterByMessageType,
      navigateToCertificateList,
      messageTypeLabels,
      formatNumber,
      formatPercentage,
      formatDate,
      displayValue,
      formatClicks,
      getDateClass,
      formatClickRatePercent,
      calculateSum,
      calculateAverage,
      calculateSumClicks,
      calculateAverageClicks,
      calculateClickRateFromSum,
      calculateConversionRateFromSum,
      // 보정치 관련
      getAdjustment,
      hasAdjustment,
      isEditing,
      startEditing,
      cancelEdit,
      confirmEditWithValue,
      calculateAdjustedSum,
    };
  },
};
</script>

<style scoped>
.message-conversion-report {
  padding: 0;
  background: transparent;
}

/* 메시지 유형별 전환 하위 탭 */
.message-conversion-sub-tabs {
  margin-top: 0;
  padding-top: 16px;
}

:deep(.message-conversion-sub-tabs .ant-tabs-nav) {
  margin-bottom: 20px;
  background: #fafafa;
  padding: 12px 16px 0;
  border-radius: 8px 8px 0 0;
}

:deep(.message-conversion-sub-tabs .ant-tabs-tab) {
  padding: 10px 20px;
  font-weight: 500;
}

:deep(.message-conversion-sub-tabs .ant-tabs-tab-active) {
  background: #fff;
  border-radius: 6px 6px 0 0;
}

:deep(.ant-table-wrapper) {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

:deep(.ant-table-thead > tr > th) {
  background: #fafafa;
  font-weight: 600;
  color: #262626;
  font-size: 13px;
  border-bottom: 2px solid #e8e8e8;
}

:deep(.ant-table-tbody > tr:hover > td) {
  background: #f5f9ff;
}

.link-text {
  color: #1890ff;
  cursor: pointer;
  text-decoration: underline;
  font-weight: 500;
  transition: all 0.2s ease;
}

.link-text:hover {
  color: #40a9ff;
  text-decoration-thickness: 2px;
}

.date-saturday {
  color: #1890ff;
  font-weight: 600;
}

.date-sunday {
  color: #f5222d;
  font-weight: 600;
}

.reservation-highlight {
  color: #f5222d !important;
  font-weight: 700 !important;
  background: #fff1f0;
  padding: 2px 6px;
  border-radius: 4px;
}

/* Summary 행 스타일 */
:deep(.summary-row) {
  background: #e6f7ff !important;
  border-top: 2px solid #1890ff;
}

:deep(.summary-row td) {
  font-weight: 600 !important;
  color: #0050b3 !important;
}
</style>
